<template>
  <div v-if="store.isShowingFilters" :class="filterAsideClass">
    <div v-if="currentPanel == 'root'" class="aside">
      <vs-wrapper>
        <vs-button
          data-test="hide-job-filter"
          icon="chevron_left"
          label="Hide filters"
          type="tertiary"
          small
          @click="store.hideFilterPanel()"
        />
        <table-search
          v-if="!hideSearch && !isMobileView"
          :search-value="store.search"
          label="Look for..."
          data-test="search-job-input"
          @update-search="store.updateSearch"
        />
        <filter-aside-date-range
          :start-date="store.start"
          :end-date="store.end"
          :max-range="dateRangeCap != null ? dateRangeCap - 1 : null"
          data-test="date-filter"
          @change="store.updateDateRange"
        >
          <template #default>
            <v-row dense justify="center" no-gutters class="selection-row">
              <v-col align-self="center" cols="auto">
                <vs-text data-test="date-filter-label" center type="subdued"
                  >{{ store.dateRangeLabel }}
                </vs-text>
              </v-col>
              <v-col
                v-if="store.dateWindowEnabled && store.hasDateRange && !store.isDefaultDateRange"
                cols="auto"
                align-self="center"
              >
                <v-btn
                  data-test="date-filter-reset"
                  icon
                  x-small
                  title="Back to the last 7 days through tomorrow"
                  aria-label="Reset dates to the last 7 days through tomorrow"
                  @click="store.resetDateRange"
                >
                  <v-icon small>mdi-restore</v-icon>
                </v-btn>
              </v-col>
              <v-col
                v-if="store.hasDateRange && !(store.dateWindowEnabled && dateRangeRequired)"
                cols="auto"
                align-self="center"
              >
                <v-btn
                  data-test="date-filter-clear"
                  icon
                  x-small
                  title="Show all dates"
                  aria-label="Show all dates"
                  @click="store.clearDateRange"
                >
                  <v-icon small color="red">mdi-close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </filter-aside-date-range>

        <vs-button
          v-if="showArchivedButton"
          :class="{
            active: showArchived,
            'show-archived-button': true,
          }"
          label="Archived"
          icon="mdi-archive-arrow-down-outline"
          type="secondary"
          :to="{ query: { archived: !showArchived } }"
          title="Show archived"
          full-width
        />
        <vs-wrapper divider space="small">
          <v-row justify="center" no-gutters class="mb-2">
            <priority-filter
              :dense="false"
              :selected-priorities="store.selectedPriorities"
              :mandatory="false"
              data-test="priority-filter"
              @on-change="store.updatePriority"
            />
          </v-row>

          <filter-aside-section
            title="Divisions"
            :items="divisionOptions"
            :selected="store.selectedDivisions"
            empty-is-all
            data-test="divisions-filter"
            @changed="store.updateDivision"
          />

          <filter-aside-section
            title="Resource types"
            :items="resourceTypeOptions"
            :selected="store.selectedResourceTypes"
            empty-is-all
            data-test="resource-type-filter"
            @changed="store.updateResourceType"
          />

          <filter-aside-section
            title="Activities"
            :items="activityOptions"
            :selected="store.selectedActivities"
            empty-is-all
            data-test="activities-filter"
            @changed="store.updateActivity"
          />

          <filter-aside-section
            v-if="worksWithMultipleServiceProviders"
            title="Service providers"
            :items="serviceProviderOptions"
            :selected="store.selectedServiceProviders"
            empty-is-all
            data-test="service-provider-filter"
            @changed="store.updateServiceProvider"
          />

          <filter-aside-section
            title="Job Status"
            :items="filterOptions?.jobFilterOptions.jobStatusOptions"
            :selected="store.selectedJobStatuses"
            empty-is-all
            data-test="job-status-filter"
            @changed="store.updateJobStatus"
          />

          <filter-aside-section
            v-if="enableBatchStatusesFilter"
            title="Batch Status"
            :items="batchStatusOptions"
            :selected="store.selectedBatchStatuses"
            empty-is-all
            data-test="batch-status-filter"
            @changed="store.updateBatchStatus"
          />

          <filter-aside-section
            v-if="hasApprovalStatusOptions"
            title="Approval Status"
            :items="filterOptions?.jobFilterOptions.approvalOptions"
            :selected="store.selectedApprovalStatuses"
            empty-is-all
            data-test="approval-status-filter"
            @changed="store.updateApprovalStatus"
          />

          <filter-aside-section
            title="Locations"
            :selected="store.selectedLocations"
            data-test="location-filter"
            empty-is-all
            no-expand
            @on-section-expand="() => (currentPanel = 'locations')"
            @changed="() => store.updateLocation([])"
          />
        </vs-wrapper>
      </vs-wrapper>
    </div>
    <div v-else-if="currentPanel == 'locations'" class="locations-aside-panel">
      <jobs-filter-locations-aside
        :selected-items="store.selectedLocations"
        @leave-panel="() => (currentPanel = 'root')"
        @save-filters="handleLocationsUpdated"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Dictionary, groupBy, orderBy, sortBy } from 'lodash'
import FilterAsideSection from '@/components/common/FilterAsideSection.vue'
import FilterAsideDateRange from '@/components/common/FilterAsideDateRange.vue'
import PriorityFilter from '@/components/common/PriorityFilter.vue'
import TableSearch from '@/components/common/TableSearch.vue'
import JobsFilterLocationsAside from '@/components/jobs/JobsFilterLocationsAside.vue'
import { useJobFilterStore } from '@/components/field-view-next/JobsFilterStore'
import { computed, ref, toRef, watchEffect } from 'vue'
import { useGetJobFilterOptions } from '@/api/filter-options'
import { useGetters, useStore } from 'vuex-composition-helpers'
import { BatchStatus } from '@/models/batch'
import { useScreen } from '@/common/useScreen'
import VsButton from '@/components/vision/VsButton.vue'
import { useFilters } from '@/components/jobs/filters'
import { useRoute } from 'vue-router/composables'
import { useJobSearchRouteContext } from '@/common/useJobSearchRouteContext'

const props = defineProps<{
  hideSearch?: boolean
  dateRangeRequired?: boolean
}>()

const currentPanel = ref<'root' | 'locations'>('root')

const store = useJobFilterStore()
// The map's cap (Settings:MaxMapDateRangeDays) only applies with the FieldViewDateWindow flag.
const dateRangeCap = computed(() =>
  props.dateRangeRequired && store.dateWindowEnabled ? store.maxDateRangeDays : null,
)
const vueXStore = useStore()
const { isMobileView } = useScreen()
const filterAsideClass = computed(() => ({
  'filter-asise': true,
  'limit-width': !isMobileView.value,
}))
const route = useRoute()
const { mainRouteName } = useJobSearchRouteContext()
const showArchivedButton = computed(() => route.name === mainRouteName.value && isMobileView.value)

const { data: filterOptions, isFetched: filterOptionsIsFetched } = useGetJobFilterOptions()

const divisionOptions = computed(() => {
  const grouped = groupBy(filterOptions.value?.jobFilterOptions.divisionOptions, (d) => d.hubName)

  return withHeaders(grouped)
})

const resourceTypeOptions = computed(() => {
  const grouped = groupBy(
    filterOptions.value?.jobFilterOptions.resourceTypeOptions,
    (d) => d.serviceName,
  )

  return withHeaders(grouped)
})

const activityOptions = computed(() => {
  const grouped = groupBy(filterOptions.value?.jobFilterOptions.activityOptions, (d) => d.hubName)

  return withHeaders(grouped)
})

const batchStatusOptions = [
  {
    id: BatchStatus.NotBatched,
    name: 'Not Batched',
  },
  {
    id: BatchStatus.Batched,
    name: 'Batched',
  },
]

const enableBatchStatusesFilter = computed<boolean>(
  () =>
    vueXStore.getters['featureFlags/invoices'] &&
    vueXStore.getters['permissions/getInvoicingEntitlement'],
)

const hasApprovalStatusOptions = computed(
  () => filterOptions.value?.jobFilterOptions.approvalOptions.length,
)

const withHeaders = function <T>(grouped: Dictionary<T[]>) {
  return orderBy(Object.keys(grouped))
    .map((groupKey) => [{ header: groupKey }, ...sortBy(grouped[groupKey])])
    .reduce((aggregate, items) => [...aggregate, ...items], [])
}

const handleLocationsUpdated = (locationIds: number[]) => {
  store.updateLocation(locationIds)
  currentPanel.value = 'root'
}

const { isHostOrganizationUser: worksWithMultipleServiceProviders } = useGetters('permissions', [
  'isHostOrganizationUser',
])

const serviceProviderOptions = computed(() => {
  return [
    { id: null, name: 'No service provider' },
    ...(filterOptions?.value?.jobFilterOptions.serviceProviderOptions ?? []),
  ]
})

// self-healing filters: reset/clear any filter states that we are not showing
// to the user. This is likely only a problem for folks who log-in/out of
// with different user accounts and or different roles/permissions.
watchEffect(() => {
  if (!filterOptionsIsFetched.value) {
    return
  }

  // reset approval status filters if not shown to users
  if (!hasApprovalStatusOptions.value && store.selectedApprovalStatuses.length > 0) {
    store.$patch({ selectedApprovalStatuses: [] })
  }

  // reset service provider filters if not shown to users
  if (!worksWithMultipleServiceProviders.value && store.selectedServiceProviders.length > 0) {
    store.$patch({ selectedServiceProviders: [] })
  }

  // reset batch statuses filter if not shown to users
  if (!enableBatchStatusesFilter.value) {
    store.$patch({ selectedBatchStatuses: [] })
  }
})

const filters = useFilters()
const showArchived = toRef(filters, 'archived')
</script>

<style scoped>
.aside {
  height: 100%;
  padding: var(--space-small) var(--space-base);
  background-color: var(--color-white);
  border-right: 1px solid var(--color-grey--lightest);
  position: relative;
  z-index: 1;
  overflow: auto;
}

.filter-aside {
  width: 100%;
}

.limit-width {
  width: 300px;
}

.locations-aside-panel {
  max-height: 100vh;
  height: 100%;
}

.show-archived-button.active {
  color: var(--color-blue);
  border: thin solid var(--color-grey--lighter);
  background-color: var(--color-blue--lighter);
}
</style>
